import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { ChevronsUp, ChevronsDown, ChevronDown, WifiOff, Signal } from 'lucide-react';

// Commercial header player — mirrors KFCStKitts PromotionalVideosView.swift
// which follows COMMERCIAL_PLAYER_SPEC.md (ported from Roomie):
//   size 0 = mini bar (64px, default, paused, "Tap to expand" overlay)
//   size 1 = half (120px, tap mini to expand + play)
//   size 2 = full (220px)
// Chevron (top-right) cycles half → full → mini; collapsing pauses.
// Tapping the video toggles play/pause (native controls off).
// Playlist plays sequentially and pauses after the last video.
// Cellular shows a "Tap to play" gate; offline shows a placeholder.
const HEIGHTS = [64, 120, 220];

function detectConnection() {
  if (typeof navigator === 'undefined') return { isConnected: true, isOnWiFi: true };
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const isConnected = navigator.onLine !== false;
  // Treat as cellular only when the browser explicitly reports it —
  // browsers without the Network Information API default to WiFi.
  const cellular =
    conn?.type === 'cellular' ||
    conn?.saveData === true ||
    ['slow-2g', '2g'].includes(conn?.effectiveType);
  return { isConnected, isOnWiFi: isConnected && !cellular };
}

function PromotionalVideosView({ selectedIsland, onVideosLoaded }) {
  const { db } = useFirebase();
  const [videos, setVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState(0); // 0 = mini, 1 = half, 2 = full
  const [net, setNet] = useState(detectConnection);
  const [cellularPlayApproved, setCellularPlayApproved] = useState(false);
  // Web autoplay policy requires muted playback until a user gesture —
  // the video runs muted behind the overlays and unmutes on first tap.
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  const currentVideo = videos[currentIndex] || videos[0];
  const height = HEIGHTS[size];
  const videoIsShowing =
    !!currentVideo && net.isConnected && (net.isOnWiFi || cellularPlayApproved);

  // ---- network monitoring (NWPathMonitor equivalent) ----
  useEffect(() => {
    const update = () => setNet(detectConnection());
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    conn?.addEventListener?.('change', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
      conn?.removeEventListener?.('change', update);
    };
  }, []);

  // Pause when the connection drops — the video keeps running behind the
  // cellular gate (it's an overlay, not a replacement), matching iOS.
  useEffect(() => {
    if (!net.isConnected) {
      videoRef.current?.pause();
      setIsPlaying(false);
    }
  }, [net.isConnected]);

  // ---- fetch playlist (island filter + position order — same as iOS) ----
  useEffect(() => {
    loadPromotionalVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIsland]);

  const loadPromotionalVideos = async () => {
    if (!db || !selectedIsland) return;
    try {
      setLoading(true);
      const q = query(
        collection(db, 'promotionalVideos'),
        where('island', '==', selectedIsland),
        where('isActive', '==', true),
        orderBy('position', 'asc'),
        limit(10)
      );
      const snapshot = await getDocs(q);
      setVideos(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setCurrentIndex(0);
      setIsPlaying(true); // always running behind the overlays
      if (onVideosLoaded) onVideosLoaded(snapshot.docs.length);
    } catch (error) {
      console.error('Error loading promotional videos:', error);
      if (onVideosLoaded) onVideosLoaded(0);
    } finally {
      setLoading(false);
    }
  };

  // ---- play/pause sync ---- (videoIsShowing in deps so this fires when the
  // <video> mounts after cellular approval)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.play().catch(() => setIsPlaying(false));
    } else {
      video.pause();
    }
  }, [isPlaying, currentIndex, videoIsShowing]);

  // Sequential playlist — advance on 'ended', pause after the last one
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleEnd = () => {
      if (currentIndex + 1 < videos.length) {
        setCurrentIndex(currentIndex + 1);
        setIsPlaying(true);
      } else {
        setIsPlaying(false);
      }
    };
    video.addEventListener('ended', handleEnd);
    return () => video.removeEventListener('ended', handleEnd);
  }, [currentIndex, videos.length]);

  // Stop playback on unmount
  useEffect(() => () => videoRef.current?.pause(), []);

  const togglePlayPause = useCallback(() => setIsPlaying(p => !p), []);

  const expandFromMini = () => {
    setIsMuted(false); // user gesture → sound allowed
    setSize(2); // ⇈ — jump back to the full player
    setIsPlaying(true);
  };

  // Chevron steps the size DOWN one level: full (⇊) → half (⌄) → mini.
  // Reaching mini pauses the video.
  const collapseSize = (e) => {
    e.stopPropagation();
    const next = Math.max(0, size - 1);
    setSize(next);
    setIsPlaying(next !== 0);
  };

  const approveCellular = () => {
    // Dismiss the overlay and expand — the video has been loading/running
    // muted underneath since mount.
    setCellularPlayApproved(true);
    setIsMuted(false);
    setSize(2);
    setIsPlaying(true);
  };

  if (loading || videos.length === 0) return null;

  return (
    <div
      className="relative w-full overflow-hidden rounded-xl bg-black transition-[height] ease-in-out"
      style={{ height, transitionDuration: '250ms' }}
    >
      {/* 1. Content layer — the video always mounts and runs behind
          whatever overlay sits on top (mini bar / data gate). */}
      {net.isConnected ? (
        <video
          ref={videoRef}
          src={currentVideo.videoURL}
          className="w-full h-full object-contain"
          playsInline
          muted={isMuted}
          autoPlay
          preload="auto"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 bg-black/70">
          <WifiOff size={22} className="text-white" />
          <span className="text-[12px] text-white/80">No connection</span>
        </div>
      )}

      {/* 2. Title overlay bottom-left — hidden in mini mode, non-interactive */}
      {size !== 0 && (
        <div className="pointer-events-none absolute bottom-0 left-0 p-3">
          <h3 className="text-[15px] font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
            {currentVideo.title}
          </h3>
        </div>
      )}

      {/* 3. Invisible play/pause tap layer — only when video is showing */}
      {videoIsShowing && (
        <button
          onClick={togglePlayPause}
          className="absolute inset-0 h-full w-full"
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
        />
      )}

      {/* Shop Now — kept from the old carousel; above the tap layer, expanded only */}
      {size !== 0 && currentVideo.targetURL && (
        <div className="absolute bottom-0 right-0 p-3">
          <a
            href={currentVideo.targetURL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="rounded-full bg-[#FF7A00] px-4 py-2 text-[15px] font-semibold text-white shadow-[0_1px_2px_rgba(0,0,0,0.3)]"
          >
            Shop Now
          </a>
        </div>
      )}

      {/* 4. Mini-mode "Tap to expand" bar — only at size 0.
          Double-up chevrons: tap jumps back to the full player. */}
      {size === 0 && (
        <button
          onClick={expandFromMini}
          className="absolute inset-0 flex w-full items-center gap-2 bg-black/45 px-3"
        >
          <span className="truncate text-left text-[15px] font-medium text-white">
            {currentVideo.title}
          </span>
          <span className="ml-auto whitespace-nowrap text-[12px] text-white/85">Tap to expand</span>
          <ChevronsUp size={20} className="shrink-0 text-white" />
        </button>
      )}

      {/* 5. Size chevron, top-right — hidden in mini mode.
          Full (2) shows ⇊, half (1) shows ⌄ — each tap steps down
          one level until it's back home. */}
      {size !== 0 && (
        <button
          onClick={collapseSize}
          className="absolute right-2 top-2 rounded-full text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]"
          aria-label={size === 2 ? 'Shrink video to half' : 'Collapse video to mini'}
        >
          {size === 2 ? <ChevronsDown size={26} /> : <ChevronDown size={26} />}
        </button>
      )}

      {/* 6. Cellular data gate — an overlay ON TOP of the running video,
          not a replacement. Tap dismisses it; the video is already
          playing underneath. */}
      {net.isConnected && !net.isOnWiFi && !cellularPlayApproved && (
        <button
          onClick={approveCellular}
          className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-1.5"
        >
          {currentVideo.thumbnailURL ? (
            <img
              src={currentVideo.thumbnailURL}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-50"
            />
          ) : (
            <div className="absolute inset-0 bg-black/70" />
          )}
          <Signal size={22} className="relative text-white" />
          <span className="relative text-[12px] font-semibold text-white">Using mobile data</span>
          <span className="relative text-[11px] text-white/80">Tap to play</span>
        </button>
      )}
    </div>
  );
}

export default PromotionalVideosView;
